import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";
import { useLocation } from "react-router-dom";

import { useCompanies } from "../../../hooks/companies";
import { useToast } from "../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import ShellCard from "../../../components/ShellCard";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { digitsOnly, formatDocument, isValidCnpj, isValidCpf } from "../../../utils/formatDoc";
import { getAcessoSftp, listarAcessosSftp, mensagemErro, previewAcessoSftp, provisionarAcessoSftp } from "../../../services/sftpAcessoApi";
import PreviewCard from "./components/PreviewCard";
import ResultadoPassos from "./components/ResultadoPassos";
import AcessoCard from "./components/AcessoCard";
import TransportadorasDoEmbarcador from "./components/TransportadorasDoEmbarcador";

// Onboarding de Transportadora (PO1-3282, épico PO1-3279; nasceu como "Acesso SFTP da
// transportadora", PO1-2167): tela admin própria, no contexto do embarcador escolhido no
// seletor de empresas (padrão de MinhasTransportadoras). Escolher a transportadora na lista
// da carteira (PO1-3186) ou informar o CNPJ de uma que ainda não está nela → preview (só
// leitura) → confirmar (apelido, contatos com TI obrigatório, modo, e-mail) → orquestrador
// (cadastro → contatos → homologação → pastas → usuário SFTP → e-mail → portal) → resumo por
// passo → card do acesso (Mostrar senha / Gerar nova senha / chaves).
// Deep links: ?embarcadorId= (passo Configuração do Onboarding de Embarcador) e
// ?idPedido= + location.state.pedido (aba Pedidos da Curadoria): pré-preenche CNPJ, apelido,
// embarcador, contatos e e-mail e já consulta.

const hintStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.82rem", marginTop: "0.4rem" };
const ESTADO_TOAST = {
  OPERACIONAL: "Onboarding concluído",
  EM_ONBOARDING: "Onboarding iniciado",
  AGUARDANDO_HOMOLOGACAO: "Aguardando homologação",
  EM_CONFIGURACAO: "Aguardando homologação",
  DEVOLVIDO: "Homologação devolvida",
  NAO_CADASTRADA: "Transportadora não cadastrada",
};
const erroStyle = {
  color: AUTOPILOT_COLORS.statusDanger,
  background: AUTOPILOT_COLORS.statusDangerBg,
  border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`,
  borderRadius: 8,
  padding: "0.6rem 0.8rem",
  fontSize: "0.84rem",
  marginTop: "0.75rem",
};

export default function AcessoSftpTransportadora() {
  const { empresa, hasEmpresaSelection, optionsEmpresa, selectEmpresa } = useCompanies();
  const { addToast } = useToast();
  const location = useLocation();
  const embarcadorId = empresa?.value || null;

  const [cnpj, setCnpj] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [erro, setErro] = useState("");
  const [provisionando, setProvisionando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [dadosAcesso, setDadosAcesso] = useState(null);
  const [loadingAcesso, setLoadingAcesso] = useState(false);
  const [lista, setLista] = useState(null);
  const [loadingLista, setLoadingLista] = useState(false);
  const deepLinkAplicado = useRef(false);
  const pedidoAplicado = useRef(false);
  const previewRef = useRef(null);
  const rolarAoAbrirPreview = useRef(false);
  // Pedido vindo da Curadoria (PO1-3283): { id, cnpj, apelido, idEmbarcador, embarcador, email, contatos }.
  const pedido = (location.state && location.state.pedido) || null;
  const idPedidoQuery = new URLSearchParams(location.search).get("idPedido");

  // Com a carteira inteira na lista (PO1-3186), o preview nasce abaixo de uma tabela
  // que pode ter dezenas de linhas. Clicar numa linha e não ver nada acontecer seria
  // o mesmo problema de antes, com outra cara — então a tela vai até o preview.
  useEffect(() => {
    if (!preview || !rolarAoAbrirPreview.current) return;
    rolarAoAbrirPreview.current = false;
    const el = previewRef.current;
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [preview]);

  // Deep link ?embarcadorId= (ou o embarcador do pedido): quando as opções carregam,
  // seleciona o embarcador (uma vez).
  useEffect(() => {
    if (deepLinkAplicado.current || !optionsEmpresa) return;
    const alvo = new URLSearchParams(location.search).get("embarcadorId") || (pedido && pedido.idEmbarcador);
    if (!alvo) return;
    deepLinkAplicado.current = true;
    if (String(embarcadorId) === String(alvo)) return;
    const opcao = optionsEmpresa.find((o) => o && String(o.value) === String(alvo));
    if (opcao) selectEmpresa(opcao);
  }, [location.search, optionsEmpresa, embarcadorId, selectEmpresa, pedido]);

  // Transportadoras do embarcador com o estado do acesso (PO1-3186): é por onde se acha
  // um acesso sem saber o CNPJ e por onde se cria o que falta.
  const carregarLista = useCallback(async () => {
    if (!embarcadorId) return;
    try {
      setLoadingLista(true);
      const d = await listarAcessosSftp(embarcadorId);
      setLista(d);
    } catch (e) {
      // A lista é um atalho: se falhar, a busca por CNPJ continua funcionando.
      setLista(null);
    } finally {
      setLoadingLista(false);
    }
  }, [embarcadorId]);

  // Troca de embarcador limpa a tela e recarrega a lista.
  useEffect(() => {
    setPreview(null);
    setResultado(null);
    setDadosAcesso(null);
    setErro("");
    setLista(null);
    carregarLista();
  }, [embarcadorId, carregarLista]);

  const docValido = (v) => {
    const d = digitsOnly(v);
    return (d.length === 14 && isValidCnpj(d)) || (d.length === 11 && isValidCpf(d));
  };

  const carregarAcesso = useCallback(
    async (idTransportadora, fresco) => {
      if (!embarcadorId || !idTransportadora) return;
      try {
        setLoadingAcesso(true);
        const d = await getAcessoSftp(embarcadorId, idTransportadora, { fresco });
        setDadosAcesso(d);
      } catch (e) {
        addToast({ place: "tr", type: "warning", title: "Acesso", description: mensagemErro(e, "Não foi possível carregar o acesso.") });
      } finally {
        setLoadingAcesso(false);
      }
    },
    [embarcadorId, addToast]
  );

  const consultar = useCallback(async (docDaLista) => {
    if (!embarcadorId) return;
    // Clique na lista manda o CNPJ direto: não dá para esperar o setState do input.
    const doc = typeof docDaLista === "string" && docDaLista ? docDaLista : cnpj;
    if (!docValido(doc)) {
      setErro("Informe um CNPJ (ou CPF) válido.");
      return;
    }
    setErro("");
    setResultado(null);
    setDadosAcesso(null);
    try {
      setLoadingPreview(true);
      const p = await previewAcessoSftp({ embarcadorId, cnpj: digitsOnly(doc) });
      setPreview(p);
      if (p.transportadora && p.vinculo && p.vinculo.existe) {
        await carregarAcesso(p.transportadora.id, false);
      }
    } catch (e) {
      setPreview(null);
      setErro(mensagemErro(e, "Falha ao consultar a transportadora."));
    } finally {
      setLoadingPreview(false);
    }
  }, [embarcadorId, cnpj, carregarAcesso]);

  // Pedido da Curadoria: com o embarcador certo selecionado, preenche o CNPJ e consulta.
  useEffect(() => {
    if (pedidoAplicado.current || !pedido || !pedido.cnpj || !embarcadorId) return;
    if (pedido.idEmbarcador && String(pedido.idEmbarcador) !== String(embarcadorId)) return;
    pedidoAplicado.current = true;
    const doc = digitsOnly(pedido.cnpj);
    setCnpj(doc);
    rolarAoAbrirPreview.current = true;
    consultar(doc);
  }, [pedido, embarcadorId, consultar]);

  const provisionar = useCallback(
    async (form) => {
      if (!embarcadorId || !preview) return;
      try {
        setProvisionando(true);
        setErro("");
        const r = await provisionarAcessoSftp({
          embarcadorId,
          cnpj: preview.cnpj,
          fantasia: form.fantasia || undefined,
          confirmarApelido: form.confirmarApelido === true,
          religarVinculo: form.religarVinculo === true,
          modoAuth: form.modoAuth,
          chaves: form.modoAuth === "ssh" ? form.chaves : undefined,
          email: form.email,
          contatos: form.contatos,
        });
        setResultado(r);
        const curadoria = r.httpStatus === 202 || r.estado === "EM_CURADORIA";
        addToast({
          place: "tr",
          type: curadoria ? "warning" : r.ok ? "success" : "warning",
          title: curadoria ? "Foi para a Curadoria" : ESTADO_TOAST[r.estado] || (r.ok ? "Onboarding iniciado" : "Onboarding com pendência"),
          description: curadoria
            ? (r.curadoria && r.curadoria.motivo) || "A matriz 0001 precisa de ajuste. Libere na Curadoria de Transportadoras."
            : r.estado === "AGUARDANDO_HOMOLOGACAO" || r.estado === "EM_CONFIGURACAO"
            ? "Transportadora nova na Rede: aprove na fila de Homologação para pastas e acesso seguirem."
            : r.emAndamento
            ? "Pastas, usuário SFTP, e-mail e portal continuam depois da resposta. Veja o resumo por passo."
            : r.ok
            ? "Todos os passos concluídos."
            : "Veja o resumo por passo.",
        });
        if (curadoria) return;
        const p = await previewAcessoSftp({ embarcadorId, cnpj: preview.cnpj });
        setPreview(p);
        if (p.transportadora) await carregarAcesso(p.transportadora.id, true);
        carregarLista();
      } catch (e) {
        setErro(mensagemErro(e, "Falha ao iniciar o onboarding."));
      } finally {
        setProvisionando(false);
      }
    },
    [embarcadorId, preview, addToast, carregarAcesso, carregarLista]
  );

  if (!hasEmpresaSelection) {
    return (
      <div className="content acesso-sftp-page">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para fazer o onboarding das transportadoras dele." />
      </div>
    );
  }

  const idTransportadora = preview?.transportadora?.id || null;

  return (
    <div className="content acesso-sftp-page">
      <PageHero
        eyebrow="Everlog Admin"
        title="Onboarding de Transportadora"
        description={`Cadastro na matriz, contatos, homologação, pastas (CT/FT/OC), usuário no Wing FTP e acesso ao portal para uma transportadora de ${empresa?.label || "embarcador"}. Reexecutar é seguro: nada é duplicado.`}
        actions={
          <PageHeroRefreshButton
            onClick={() => {
              carregarLista();
              if (preview) consultar();
            }}
            loading={loadingPreview || loadingLista}
          />
        }
      />

      <TransportadorasDoEmbarcador
        itens={lista?.itens}
        legadoConferido={lista?.legadoConferido}
        host={lista?.host}
        porta={lista?.porta}
        loading={loadingLista}
        selecionadoId={preview?.transportadora?.id}
        onSelecionar={(item) => {
          const doc = digitsOnly((item.transportadora && item.transportadora.cnpj) || "");
          setCnpj(doc);
          rolarAoAbrirPreview.current = true;
          consultar(doc);
        }}
      />

      <ShellCard eyebrow="Transportadora" title="Transportadora fora da carteira" style={{ marginTop: "1rem" }}>
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
          <Input
            value={formatDocument(cnpj)}
            onChange={(e) => setCnpj(digitsOnly(e.target.value).slice(0, 14))}
            onKeyDown={(e) => { if (e.key === "Enter") consultar(); }}
            placeholder="00.000.000/0000-00"
            style={{ maxWidth: 260 }}
            aria-label="CNPJ da transportadora"
          />
          <Button color="primary" onClick={consultar} disabled={loadingPreview || !cnpj}>
            {loadingPreview ? <Spinner size="sm" /> : "Consultar"}
          </Button>
        </div>
        <div style={hintStyle}>
          Para quem ainda não está na lista acima. A transportadora do vínculo é sempre a matriz: filial informada cai na matriz dela; a pasta usa o apelido confirmado aqui e não muda depois.
        </div>
        {erro ? <div style={erroStyle}>{erro}</div> : null}
      </ShellCard>

      <div ref={previewRef}>
        {preview ? (
          <PreviewCard
            preview={preview}
            acesso={dadosAcesso?.acesso || preview.acesso}
            onProvisionar={provisionar}
            provisionando={provisionando}
            inicial={pedido && (!idPedidoQuery || String(pedido.id) === String(idPedidoQuery)) ? pedido : null}
          />
        ) : null}
      </div>

      {resultado ? <ResultadoPassos resultado={resultado} /> : null}

      {preview && idTransportadora && preview.vinculo?.existe ? (
        <AcessoCard
          idEmbarcador={embarcadorId}
          idTransportadora={idTransportadora}
          dados={dadosAcesso}
          loading={loadingAcesso}
          emailSugerido={preview.emailSugerido}
          onChanged={() => carregarAcesso(idTransportadora, true)}
        />
      ) : null}
    </div>
  );
}
